// Custom created component - not part of original Catalyst UI
// HybridLayout combines persistent sidebar with always-visible navbar

import * as Headless from '@headlessui/react'
import { useCallback, useMemo, useState, createContext, useContext } from 'react'
import { NavbarItem } from '@/components/catalyst-ui/navbar'

const SidebarContext = createContext(null)
const SIDEBAR_WIDTH = {
  expanded: 'w-64',
  collapsed: 'w-16',
}
const CONTENT_OFFSET = {
  expanded: 'lg:ml-64',
  collapsed: 'lg:ml-16',
}
const COLLAPSED_SIDEBAR_CONTENT_CLASS_NAME = [
  '[&_[data-sidebar-label]]:pointer-events-none',
  '[&_[data-sidebar-label]]:w-0',
  '[&_[data-sidebar-label]]:opacity-0',
  '[&_[data-sidebar-trailing]]:hidden',
  '[&_[data-sidebar-children]]:hidden',
  '[&_[data-sidebar-extra]]:hidden',
  '[&_[data-sidebar-footer]]:p-2.5',
  '[&_[data-sidebar-footer]_[data-sidebar-item-wrapper]]:mx-auto',
  '[&_[data-sidebar-footer]_[data-sidebar-item-wrapper]]:w-fit',
  '[&_[data-sidebar-footer]_[data-sidebar-item]]:w-fit',
  '[&_[data-sidebar-footer]_[data-sidebar-item]]:gap-0',
  '[&_[data-sidebar-footer]_[data-sidebar-item]]:px-2',
].join(' ')

export function useSidebar() {
  return useContext(SidebarContext)
}

function OpenMenuIcon() {
  return (
    <svg data-slot="icon" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M2 6.75C2 6.33579 2.33579 6 2.75 6H17.25C17.6642 6 18 6.33579 18 6.75C18 7.16421 17.6642 7.5 17.25 7.5H2.75C2.33579 7.5 2 7.16421 2 6.75ZM2 13.25C2 12.8358 2.33579 12.5 2.75 12.5H17.25C17.6642 12.5 18 12.8358 18 13.25C18 13.6642 17.6642 14 17.25 14H2.75C2.33579 14 2 13.6642 2 13.25Z" />
    </svg>
  )
}

function CloseMenuIcon() {
  return (
    <svg data-slot="icon" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
    </svg>
  )
}

function MobileSidebar({ open, close, children }) {
  return (
    <Headless.Dialog open={open} onClose={close} className="lg:hidden">
      <Headless.DialogBackdrop
        transition
        className="fixed inset-0 bg-black/30 transition data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />
      <Headless.DialogPanel
        transition
        className="fixed inset-y-0 w-full max-w-80 p-2 transition duration-300 ease-in-out data-closed:-translate-x-full"
      >
        <div className="flex h-full flex-col rounded-lg bg-[#f4f4f5] shadow-xs ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
          <div className="-mb-3 px-4 pt-3">
            <Headless.CloseButton as={NavbarItem} aria-label="Close navigation">
              <CloseMenuIcon />
            </Headless.CloseButton>
          </div>
          {children}
        </div>
      </Headless.DialogPanel>
    </Headless.Dialog>
  )
}

export function HybridLayout({
  navbar,
  sidebar,
  children,
  hideSidebar = false,
  topBanner = null,
  contentMaxWidthClassName,
}) {
  let [showSidebar, setShowSidebar] = useState(false)
  let [sidebarHovered, setSidebarHovered] = useState(false)
  const [activeCollapsibleItems, setActiveCollapsibleItems] = useState({})
  const closeSidebar = useCallback(() => setShowSidebar(false), [])
  const setCollapsibleSidebarItemActive = useCallback((id, active) => {
    setActiveCollapsibleItems((current) => {
      if (active) {
        if (current[id]) return current
        return { ...current, [id]: true }
      }
      if (!current[id]) return current
      const next = { ...current }
      delete next[id]
      return next
    })
  }, [])
  const hasTopBanner = Boolean(topBanner)
  const collapsibleSidebar = Object.values(activeCollapsibleItems).some(Boolean)
  const desktopSidebarExpanded = !collapsibleSidebar || sidebarHovered
  const desktopSidebarWidth = desktopSidebarExpanded ? SIDEBAR_WIDTH.expanded : SIDEBAR_WIDTH.collapsed
  const desktopContentOffset = desktopSidebarExpanded ? CONTENT_OFFSET.expanded : CONTENT_OFFSET.collapsed
  const resolvedContentMaxWidthClassName = contentMaxWidthClassName ?? (collapsibleSidebar ? 'max-w-[1600px]' : 'max-w-6xl')
  const collapsedSidebarContentClassName = collapsibleSidebar && !desktopSidebarExpanded
    ? COLLAPSED_SIDEBAR_CONTENT_CLASS_NAME
    : ''
  const sidebarContextValue = useMemo(() => ({
    closeSidebar,
    setCollapsibleSidebarItemActive,
  }), [closeSidebar, setCollapsibleSidebarItemActive])

  return (
    <SidebarContext.Provider value={sidebarContextValue}>
      <div
        className={`relative isolate flex min-h-svh w-full flex-col bg-white dark:bg-zinc-900 ${!hideSidebar ? 'lg:bg-zinc-100 dark:lg:bg-zinc-950' : ''}`}
        style={{ '--workspace-sidebar-width': desktopSidebarExpanded ? '16rem' : '4rem' }}
      >
        {/* Sidebar on mobile - always available */}
        <MobileSidebar open={showSidebar} close={closeSidebar}>
          {sidebar}
        </MobileSidebar>

        {/* Fixed header on top */}
        <header className="fixed top-0 left-0 right-0 z-20 bg-[#27272A]">
          <div className="flex h-16 items-center border-b border-zinc-800 px-1 sm:px-4 dark:border-zinc-800">
            {/* Hamburger menu - always visible on mobile */}
            <div className="py-2.5 lg:hidden dark">
              <NavbarItem
                onClick={() => setShowSidebar(true)}
                aria-label="Open navigation"
              >
                <OpenMenuIcon />
              </NavbarItem>
            </div>
            <div className="min-w-0 flex-1 dark">{navbar}</div>
          </div>
          {topBanner}
        </header>

        <div className={`flex flex-1 ${hasTopBanner ? 'pt-26' : 'pt-16'}`}>
          {/* Fixed sidebar on desktop - positioned below navbar */}
          {!hideSidebar && (
            <div
              onMouseEnter={() => setSidebarHovered(true)}
              onMouseLeave={() => setSidebarHovered(false)}
              className={`fixed bottom-0 left-0 max-lg:hidden bg-white border-r border-zinc-200 dark:bg-zinc-900 dark:border-zinc-800 z-10 overflow-hidden transition-[width] duration-200 ease-out ${desktopSidebarWidth} ${collapsedSidebarContentClassName} ${hasTopBanner ? 'top-[104px]' : 'top-16'}`}
            >
              {sidebar}
            </div>
          )}

          {/* Content area - positioned to the right of sidebar on desktop */}
          <main className={`flex flex-1 flex-col overflow-x-hidden transition-[margin] duration-200 ease-out ${!hideSidebar ? desktopContentOffset : ''}`}>
            {hideSidebar ? (
              <div className="grow">{children}</div>
            ) : (
              <div className="grow p-6 lg:bg-white lg:p-10 lg:shadow-xs lg:ring-1 lg:ring-zinc-950/5 dark:lg:bg-zinc-900 dark:lg:ring-white/10">
                <div className={`mx-auto w-full ${resolvedContentMaxWidthClassName}`}>{children}</div>
              </div>
            )}
          </main>
        </div>
      </div>
    </SidebarContext.Provider>
  )
}
